import { useMemo, useState } from 'react';
import { CpmButton, CpmErrorCard } from '../../../../components/design-system';
import { formatStoreCounts } from '../../../../constants/centralProductMaster.constants';
import type {
    CpmExciseCategory,
    CpmExciseStandardVolume,
    CpmState,
} from '../../../../types/centralProductMaster.types';
import CategoryCard from './CategoryCard';
import LicenseTypesPanel from './LicenseTypesPanel';

interface StateDetailViewProps {
    state: CpmState;
    categories: CpmExciseCategory[];
    loading: boolean;
    error: string | null;
    busy?: boolean;
    onBack: () => void;
    onOpenAdd: () => void;
    onRename: (cat: CpmExciseCategory, name: string) => Promise<void> | void;
    onToggleStatus: (cat: CpmExciseCategory) => Promise<void> | void;
    onAskDelete: (cat: CpmExciseCategory) => void;
    onAddSub: (cat: CpmExciseCategory, name: string) => Promise<void> | void;
    onRenameSub: (cat: CpmExciseCategory, subId: string, name: string) => Promise<void> | void;
    onRemoveSub: (cat: CpmExciseCategory, subId: string) => Promise<void> | void;
    onRefreshSizes: (cat: CpmExciseCategory) => Promise<void>;
    onSetStandardVolumes: (
        cat: CpmExciseCategory,
        volumes: CpmExciseStandardVolume[]
    ) => Promise<void>;
    onRetry: () => void;
    onLicenseTypesSaved: () => void;
}

const CARD_MIN_WIDTH = 420;

export default function StateDetailView({
    state,
    categories,
    loading,
    error,
    busy = false,
    onBack,
    onOpenAdd,
    onRename,
    onToggleStatus,
    onAskDelete,
    onAddSub,
    onRenameSub,
    onRemoveSub,
    onRefreshSizes,
    onSetStandardVolumes,
    onRetry,
    onLicenseTypesSaved,
}: StateDetailViewProps) {
    const [query, setQuery] = useState('');
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const list = categories;
    const q = query.trim().toLowerCase();
    const filtered = useMemo(() => {
        if (!q) return list;
        return list.filter(
            (c) =>
                c.category.toLowerCase().includes(q) ||
                (c.subCategories ?? []).some((s) => s.name.toLowerCase().includes(q))
        );
    }, [list, q]);

    const anyOpen = filtered.some((c) => expanded[c._id]);
    // Curated = at least one ACTIVE category — same gate the linking pipeline uses
    // (ExciseCategory.findOne({ state, status: 'ACTIVE' })). An INACTIVE-only list
    // must not read as curated even though the records exist.
    const activeCount = list.filter((c) => (c.status || 'ACTIVE') === 'ACTIVE').length;
    const isCurated = activeCount > 0;
    const ready = state.isLive && isCurated;
    const subTotal = list.reduce((a, c) => a + (c.subCategories?.length ?? 0), 0);

    const badgeLabel = isCurated ? 'Curated' : state.isLive ? 'Needs setup' : 'Not live';
    const badgeTone = isCurated ? 'ok' : state.isLive ? 'warn' : 'off';

    const categoriesPending = loading && list.length === 0;
    const categoriesFailed = Boolean(error) && list.length === 0;

    const selSummary = categoriesPending
        ? 'Loading categories'
        : list.length === 0
          ? 'No categories yet'
          : `${list.length} ${list.length === 1 ? 'category' : 'categories'} · ${activeCount} active · ${subTotal} subcategories`;

    const readyLabel = ready
        ? `Eligible for automatic state-product creation — ${state.name} is live and has ${activeCount} curated ${activeCount === 1 ? 'category' : 'categories'}.`
        : state.isLive
          ? `Not eligible yet — ${state.name} is live but has no curated excise category. Add one to unblock state-product creation for its linked variants.`
          : isCurated
            ? `Not eligible yet — categories are curated but ${state.name} is not marked live. Both are required before state-product rows are created.`
            : `Not eligible yet — ${state.name} is neither marked live nor curated. Both are required before state-product rows are created.`;

    const backHint = list.length
        ? `Excise categories below apply to ${state.name} only.`
        : state.isLive
          ? `${state.name} is live and still needs its category list, so its state-product rows are being skipped.`
          : `${state.name} is not live, so nothing is being created for it either way.`;

    const listHeading = q
        ? `${filtered.length} of ${list.length} categories match`
        : `${list.length} ${list.length === 1 ? 'excise category' : 'excise categories'}`;

    const toggleAll = () => {
        const next: Record<string, boolean> = { ...expanded };
        filtered.forEach((c) => {
            next[c._id] = !anyOpen;
        });
        setExpanded(next);
    };

    return (
        <div className="flex flex-col min-w-0" style={{ gap: 16 }}>
            <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
                <CpmButton
                    variant="outline"
                    size="sm"
                    onClick={onBack}
                    leadingIcon={
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path d="m15 18-6-6 6-6" />
                        </svg>
                    }
                >
                    All states
                </CpmButton>
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--cpm-text-tertiary)',
                    }}
                >
                    {backHint}
                </span>
            </div>

            <div
                className="rounded-[16px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-card)] flex items-center flex-wrap"
                style={{
                    boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                    padding: 20,
                    gap: 18,
                }}
            >
                <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <h2 className="m-0 !text-[22px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-brand-navy)]">
                            {state.name}
                        </h2>
                        <span
                            className="inline-flex items-center rounded-full"
                            style={{
                                gap: 6,
                                padding: '4px 11px',
                                fontSize: 11.5,
                                fontWeight: 800,
                                background:
                                    badgeTone === 'ok'
                                        ? 'var(--cpm-success-bg)'
                                        : badgeTone === 'warn'
                                          ? 'var(--cpm-warning-bg)'
                                          : 'var(--cpm-surface-subtle)',
                                border: `1px solid ${
                                    badgeTone === 'ok'
                                        ? 'var(--cpm-success-border)'
                                        : badgeTone === 'warn'
                                          ? 'var(--cpm-warning-border)'
                                          : 'var(--cpm-border-default)'
                                }`,
                                color:
                                    badgeTone === 'ok'
                                        ? 'var(--cpm-success-text-light)'
                                        : badgeTone === 'warn'
                                          ? 'var(--cpm-warning-text)'
                                          : 'var(--cpm-text-secondary)',
                            }}
                        >
                            {badgeLabel}
                        </span>
                        <span
                            className="inline-flex items-center rounded-full border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)] text-[var(--cpm-text-secondary)] tabular"
                            style={{
                                gap: 6,
                                padding: '4px 11px',
                                fontSize: 11.5,
                                fontWeight: 800,
                            }}
                        >
                            {formatStoreCounts(
                                state.activeStoreCount || 0,
                                state.trialStoreCount || 0
                            )}
                        </span>
                    </div>
                    <span
                        style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {selSummary}
                    </span>
                    <span
                        className="inline-flex items-start max-w-[640px]"
                        style={{
                            gap: 8,
                            marginTop: 2,
                            padding: '7px 11px',
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1.45,
                            background: ready ? 'var(--cpm-success-bg)' : 'var(--cpm-warning-bg)',
                            border: `1px solid ${ready ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                            color: ready
                                ? 'var(--cpm-success-text-light)'
                                : 'var(--cpm-warning-text)',
                        }}
                    >
                        <span
                            className="shrink-0 rounded-full"
                            style={{
                                marginTop: 5,
                                width: 7,
                                height: 7,
                                background: ready
                                    ? 'var(--cpm-success-dot)'
                                    : 'var(--cpm-warning-dot)',
                            }}
                        />
                        {readyLabel}
                    </span>
                </div>
                <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                    <span
                        className="inline-flex items-center rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white"
                        style={{ gap: 8, height: 40, padding: '0 13px' }}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--cpm-text-tertiary)"
                            strokeWidth="2.2"
                            className="shrink-0"
                        >
                            <circle cx="11" cy="11" r="7" />
                            <path d="m21 21-4.3-4.3" />
                        </svg>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter categories or subcategories"
                            className="border-none outline-none bg-transparent text-[var(--cpm-brand-navy)]"
                            style={{ width: 230, fontSize: 13, fontWeight: 700 }}
                        />
                    </span>
                    <CpmButton variant="outline" size="lg" onClick={toggleAll}>
                        {anyOpen ? 'Collapse all' : 'Expand all'}
                    </CpmButton>
                    <CpmButton
                        variant="navy"
                        size="lg"
                        onClick={onOpenAdd}
                        leadingIcon={
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                            >
                                <path d="M12 5v14" />
                                <path d="M5 12h14" />
                            </svg>
                        }
                    >
                        Add category
                    </CpmButton>
                </div>
            </div>

            <LicenseTypesPanel key={state._id} state={state} onSaved={onLicenseTypesSaved} />

            {categoriesPending ? (
                <div className="flex flex-col" style={{ gap: 16 }} aria-busy="true">
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <span className="cpm-spin" />
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Loading {state.name} categories
                        </span>
                    </div>
                    <div
                        className="grid"
                        style={{
                            gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
                            gap: 12,
                        }}
                    >
                        {Array.from({ length: 6 }, (_, i) => (
                            <div
                                key={i}
                                className="border border-[var(--cpm-border-default)] rounded-[12px] bg-[var(--cpm-surface-card)] flex flex-col"
                                style={{ gap: 11, padding: '14px 16px' }}
                            >
                                <div className="flex items-center" style={{ gap: 11 }}>
                                    <span
                                        className="cpm-sk"
                                        style={{ width: 28, height: 28, borderRadius: 8 }}
                                    />
                                    <span
                                        className="cpm-sk"
                                        style={{ height: 15, width: `${42 + ((i * 11) % 40)}%` }}
                                    />
                                    <span
                                        className="cpm-sk ml-auto"
                                        style={{ height: 20, width: 74, borderRadius: 999 }}
                                    />
                                </div>
                                <div className="flex" style={{ gap: 6, paddingLeft: 39 }}>
                                    <span
                                        className="cpm-sk"
                                        style={{ height: 18, width: 66, borderRadius: 999 }}
                                    />
                                    <span
                                        className="cpm-sk"
                                        style={{ height: 18, width: 58, borderRadius: 999 }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : categoriesFailed ? (
                <CpmErrorCard
                    title={`Couldn't load ${state.name}`}
                    body="Its excise categories didn't come back, so nothing is shown rather than showing an incomplete list. Its curated categories are untouched. License types above can still be edited."
                    onRetry={onRetry}
                    retryLabel="Try again"
                    retrying={loading}
                />
            ) : list.length > 0 ? (
                <div className="flex flex-col" style={{ gap: 10 }}>
                    <div className="flex items-center" style={{ gap: 10, padding: '0 4px' }}>
                        <span
                            className="uppercase text-[var(--cpm-text-secondary)]"
                            style={{
                                fontSize: 10.5,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                            }}
                        >
                            {listHeading}
                        </span>
                        <span
                            className="flex-1 bg-[var(--cpm-border-default)]"
                            style={{ height: 1 }}
                        />
                        <span
                            className="font-mono text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11.5, fontWeight: 700 }}
                        >
                            category / subCategories / status
                        </span>
                    </div>

                    <div
                        className="grid items-start"
                        style={{
                            gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
                            gap: 12,
                        }}
                    >
                        {filtered.map((c) => (
                            <CategoryCard
                                key={c._id}
                                category={c}
                                stateName={state.name}
                                expanded={!!expanded[c._id]}
                                showChips
                                busy={busy}
                                onToggle={() => setExpanded((s) => ({ ...s, [c._id]: !s[c._id] }))}
                                onRename={(name) => onRename(c, name)}
                                onToggleStatus={() => onToggleStatus(c)}
                                onAskDelete={() => onAskDelete(c)}
                                onAddSub={(name) => onAddSub(c, name)}
                                onRenameSub={(subId, name) => onRenameSub(c, subId, name)}
                                onRemoveSub={(subId) => onRemoveSub(c, subId)}
                                onRefreshSizes={() => onRefreshSizes(c)}
                                onSetStandardVolumes={(_cat, volumes) =>
                                    onSetStandardVolumes(c, volumes)
                                }
                            />
                        ))}
                    </div>

                    {filtered.length === 0 ? (
                        <div
                            className="rounded-[12px] border-[1.5px] border-dashed border-[var(--cpm-border-strong)] bg-[var(--cpm-surface-card)] flex flex-col items-center text-center"
                            style={{ padding: 26, gap: 8 }}
                        >
                            <span
                                className="text-[var(--cpm-brand-navy)]"
                                style={{ fontSize: 14, fontWeight: 800 }}
                            >
                                Nothing matches &ldquo;{query}&rdquo;
                            </span>
                            <CpmButton variant="outline" size="sm" onClick={() => setQuery('')}>
                                Clear filter
                            </CpmButton>
                        </div>
                    ) : null}
                </div>
            ) : (
                <div
                    className="rounded-[16px] border-[1.5px] border-dashed flex flex-col items-center text-center"
                    style={{
                        borderColor: state.isLive
                            ? 'var(--cpm-warning-border)'
                            : 'var(--cpm-border-strong)',
                        background: state.isLive
                            ? 'var(--cpm-warning-bg)'
                            : 'var(--cpm-surface-card)',
                        padding: '44px 32px',
                        gap: 14,
                    }}
                >
                    <span
                        className="inline-flex items-center justify-center bg-white"
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: 18,
                            border: `1px solid ${state.isLive ? 'var(--cpm-warning-border)' : 'var(--cpm-border-default)'}`,
                            color: state.isLive
                                ? 'var(--cpm-warning-icon)'
                                : 'var(--cpm-brand-purple)',
                        }}
                    >
                        <svg
                            width="30"
                            height="30"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                        >
                            <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
                            <path d="M2 12.18a1 1 0 0 0 .6.91l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 .6-.92" />
                            <path d="M2 17.18a1 1 0 0 0 .6.91l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 .6-.92" />
                        </svg>
                    </span>
                    <h3
                        className={`m-0 !text-[20px] !font-extrabold !tracking-[-0.02em] ${
                            state.isLive
                                ? '!text-[var(--cpm-warning-text)]'
                                : '!text-[var(--cpm-brand-navy)]'
                        }`}
                    >
                        {state.name} has no excise categories yet
                    </h3>
                    <p className="m-0 max-w-[520px] !text-[13.5px] !font-semibold !leading-[1.55] !text-[var(--cpm-text-secondary)]">
                        {state.isLive
                            ? `${state.name} is marked live, so the one thing still missing is this list. Until at least one category is curated, the linking pipeline creates no state-product pricing or excise rows for ${state.name}\u2019s linked variants — it skips them silently and retries on a later run. Add the first category from the state excise department\u2019s own list; a state is usually done in a single sitting.`
                            : `${state.name} isn\u2019t marked live, so nothing is being created for it yet. Mark it live above and curate its categories to make it eligible.`}
                    </p>
                    <CpmButton
                        variant="navy"
                        size="lg"
                        onClick={onOpenAdd}
                        leadingIcon={
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                            >
                                <path d="M12 5v14" />
                                <path d="M5 12h14" />
                            </svg>
                        }
                    >
                        Add the first category
                    </CpmButton>
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--cpm-text-tertiary)',
                        }}
                    >
                        Work from the state excise department&apos;s own category list. Nothing here
                        is generated for you.
                    </span>
                </div>
            )}
        </div>
    );
}
